/**
 * Business Comparison Page
 * 
 * Compare performance metrics across multiple businesses
 */

import React from 'react'
import { Card, Row, Col, Typography, Spin, Alert, Select, Space } from 'antd'
import { Radar, DualAxes } from '@ant-design/charts'
import { useGetDashboardQuery } from '../services/corporate'

const { Title, Text } = Typography
const { Option } = Select

const BusinessComparison: React.FC = () => {
  const { data, isLoading, error } = useGetDashboardQuery()
  const [selectedBusinesses, setSelectedBusinesses] = React.useState<string[]>([])

  React.useEffect(() => {
    if (data && data.businesses.length > 0) {
      // Auto-select first 3 businesses
      setSelectedBusinesses(
        data.businesses.slice(0, Math.min(3, data.businesses.length)).map((b) => b.business.id)
      )
    }
  }, [data])

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading comparison data...</div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <Alert
        message="Error Loading Data"
        description="Failed to load comparison data. Please try again later."
        type="error"
        showIcon
      />
    )
  }

  const { businesses } = data

  // Filter selected businesses
  const filteredBusinesses = businesses.filter((b) =>
    selectedBusinesses.includes(b.business.id)
  )

  // Prepare radar chart data (normalized metrics)
  const maxRevenue = Math.max(...businesses.map((b) => b.metrics.revenue))
  const maxJobs = Math.max(...businesses.map((b) => b.metrics.jobs))
  const maxCustomers = Math.max(...businesses.map((b) => b.metrics.customers))
  const maxInvoices = Math.max(...businesses.map((b) => b.metrics.invoices))

  const radarData = filteredBusinesses.flatMap((b) => [
    {
      business: b.business.name,
      metric: 'Revenue',
      value: maxRevenue > 0 ? (b.metrics.revenue / maxRevenue) * 100 : 0,
    },
    {
      business: b.business.name,
      metric: 'Jobs',
      value: maxJobs > 0 ? (b.metrics.jobs / maxJobs) * 100 : 0,
    },
    {
      business: b.business.name,
      metric: 'Customers',
      value: maxCustomers > 0 ? (b.metrics.customers / maxCustomers) * 100 : 0,
    },
    {
      business: b.business.name,
      metric: 'Invoices',
      value: maxInvoices > 0 ? (b.metrics.invoices / maxInvoices) * 100 : 0,
    },
  ])

  // Prepare dual axes data (revenue vs jobs)
  const dualAxesData = filteredBusinesses.map((b) => ({
    business: b.business.name,
    revenue: b.metrics.revenue,
    jobs: b.metrics.jobs,
  }))

  return (
    <div>
      <Title level={2}>Business Comparison</Title>

      {/* Business Selector */}
      <Card style={{ marginBottom: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text strong>Select Businesses to Compare:</Text>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Select businesses"
            value={selectedBusinesses}
            onChange={setSelectedBusinesses}
            maxTagCount={3}
          >
            {businesses.map((b) => (
              <Option key={b.business.id} value={b.business.id}>
                {b.business.name}
              </Option>
            ))}
          </Select>
        </Space>
      </Card>

      {selectedBusinesses.length === 0 ? (
        <Alert
          message="No Businesses Selected"
          description="Please select at least one business to compare."
          type="info"
          showIcon
        />
      ) : (
        <>
          {/* Performance Radar Chart */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={12}>
              <Card title="Performance Comparison (Normalized)" bordered={false}>
                <Radar
                  data={radarData}
                  xField="metric"
                  yField="value"
                  seriesField="business"
                  meta={{
                    value: {
                      alias: 'Performance Score',
                      min: 0,
                      max: 100,
                      formatter: (v: number) => `${v.toFixed(0)}%`,
                    },
                  }}
                  xAxis={{
                    line: null,
                    tickLine: null,
                  }}
                  yAxis={{
                    label: {
                      formatter: (v: string) => `${v}%`,
                    },
                    grid: {
                      alternateColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                  point={{
                    size: 4,
                  }}
                  area={{}}
                />
              </Card>
            </Col>

            {/* Revenue vs Jobs Comparison */}
            <Col xs={24} lg={12}>
              <Card title="Revenue vs Jobs" bordered={false}>
                <DualAxes
                  data={[dualAxesData, dualAxesData]}
                  xField="business"
                  yField={['revenue', 'jobs']}
                  geometryOptions={[
                    {
                      geometry: 'column',
                      color: '#5B8FF9',
                    },
                    {
                      geometry: 'line',
                      color: '#5AD8A6',
                      lineStyle: {
                        lineWidth: 2,
                      },
                    },
                  ]}
                  meta={{
                    revenue: {
                      alias: 'Revenue',
                      formatter: (v: number) =>
                        new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          notation: 'compact',
                        }).format(v),
                    },
                    jobs: {
                      alias: 'Jobs',
                    },
                  }}
                />
              </Card>
            </Col>
          </Row>

          {/* Detailed Metrics Comparison */}
          <Card title="Detailed Metrics" bordered={false}>
            <Row gutter={[16, 16]}>
              {filteredBusinesses.map((b) => (
                <Col xs={24} md={12} lg={8} key={b.business.id}>
                  <Card
                    title={b.business.name}
                    bordered
                    style={{ height: '100%' }}
                    headStyle={{ backgroundColor: '#f0f2f5' }}
                  >
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                      <div>
                        <Text type="secondary">Revenue</Text>
                        <div>
                          <Text strong style={{ fontSize: 20, color: '#3f8600' }}>
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                            }).format(b.metrics.revenue)}
                          </Text>
                        </div>
                      </div>
                      <div>
                        <Text type="secondary">Jobs</Text>
                        <div>
                          <Text strong style={{ fontSize: 18 }}>
                            {b.metrics.jobs.toLocaleString()}
                          </Text>
                        </div>
                      </div>
                      <div>
                        <Text type="secondary">Customers</Text>
                        <div>
                          <Text strong style={{ fontSize: 18 }}>
                            {b.metrics.customers.toLocaleString()}
                          </Text>
                        </div>
                      </div>
                      <div>
                        <Text type="secondary">Invoices</Text>
                        <div>
                          <Text strong style={{ fontSize: 18 }}>
                            {b.metrics.invoices.toLocaleString()}
                          </Text>
                        </div>
                      </div>
                      <div>
                        <Text type="secondary">Avg Revenue per Job</Text>
                        <div>
                          <Text strong style={{ fontSize: 16 }}>
                            {b.metrics.jobs > 0
                              ? new Intl.NumberFormat('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                }).format(b.metrics.revenue / b.metrics.jobs)
                              : '$0.00'}
                          </Text>
                        </div>
                      </div>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </>
      )}
    </div>
  )
}

export default BusinessComparison

